import { useState, useCallback } from 'react';
import { useAppDispatch } from '@/app/store/hooks';
import { fetchSiteData } from '@/entities/site_data/api/fetchSiteData';

interface PreloadStep {
  id: string;
  name: string;
  weight: number;
  execute: () => Promise<void>;
}

interface UseAppPreloaderReturn {
  progress: number;
  status: string;
  isComplete: boolean;
  error: string | null;
  startPreloading: () => void;
}

export const useAppPreloader = (): UseAppPreloaderReturn => {
  const dispatch = useAppDispatch();

  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('Загрузка данных...');
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isStarted, setIsStarted] = useState(false);

  // Создаем шаги предзагрузки
  const createPreloadSteps = useCallback((): PreloadStep[] => [
    {
      id: 'site-content',
      name: 'Загрузка контента сайта',
      weight: 50,
      execute: async () => {
        const result = await dispatch(fetchSiteData());
        if (fetchSiteData.rejected.match(result)) {
          throw new Error('Ошибка загрузки контента сайта');
        }
      },
    },
  ], [dispatch]);

  // Старт предзагрузки
  const startPreloading = useCallback(async () => {
    if (isStarted) return;
    setIsStarted(true);
    setProgress(0);
    setStatus('Загрузка данных...');
    setIsComplete(false);
    setError(null);

    const steps = createPreloadSteps();
    let currentProgress = 0;

    // Таймаут на случай зависания
    const maxPreloadTime = setTimeout(() => {
      console.warn('Preloader завис, форсируем завершение');
      setProgress(100);
      setStatus('Готово!');
      setIsComplete(true);
    }, 10000);

    try {
      for (const step of steps) {
        setStatus(step.name);

        try {
          await step.execute();
        } catch (stepError: any) {
          console.error(`Шаг "${step.id}" завершился ошибкой:`, stepError);
          setError(stepError.message || 'Неизвестная ошибка');
        }

        currentProgress += step.weight;
        setProgress(Math.min(currentProgress, 100));

        // Маленькая задержка для плавности прогресса
        await new Promise((r) => setTimeout(r, 100));
      }
    } finally {
      clearTimeout(maxPreloadTime);
      setProgress(100);
      setStatus('Готово!');
      setIsComplete(true);
    }
  }, [isStarted, createPreloadSteps]);

  return { progress, status, isComplete, error, startPreloading };
};
